import React, { useEffect, useState } from 'react';
import { ReactComponent as ArrowBackIcon } from 'assets/icons/arrow-back.svg';
import { Button, Dropdown, Input, Switch, ToastMessage } from '@cokitchen/cokitchen-components';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import useDashboardContext from 'contexts/dashboard.context';
import SelectDropdown from 'components/base/select-dropdown';
import BirthdayInput from 'components/base/birthday-input';
import CurrencyInput from 'components/base/currency-input';
import { ROUTE_KEYS } from 'utils/constants';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ReactComponent as ToolTipIcon } from 'assets/icons/tooltip.svg';
import { completeAssignAdminStep, handleApiError } from 'utils/functions';
import { MemberListType } from 'models/member.model';

type Props = {
  setEditMode: (value: boolean) => void;
  singlePeople?: MemberListType;
  setSelectedPeople: (value: MemberListType[]) => void;
  onClose: () => void;
  updatePerson: (newData: MemberListType) => void;
  onRefresh: () => void;
  previousPage: string;
};

type FormDataType = {
  first_name?: string;
  last_name?: string;
  mobile_number?: string;
  birthday?: string | Date;
  department_id: string;
  budget?: string;
  budget_duration?: string;
  role?: string;
  budget_setting?: string;
  email?: string;
};

const budgetDurationOptions = [
  { name: 'Daily', id: 'Daily' },
  { name: 'Weekly', id: 'Weekly' },
  { name: 'Monthly', id: 'Monthly' }
];

const EditDetails = ({
  setEditMode,
  singlePeople,
  setSelectedPeople,
  onClose,
  updatePerson,
  previousPage
}: Props) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedDepartmentName, setSelectedDepartmentName] = useState(
    singlePeople?.department?.name || ''
  );
  const [loading, setLoading] = useState(false);
  const { activeCompany, refetchActiveCompany } = useDashboardContext();
  const companyId = activeCompany?.id;
  const [departmentOptions, setDepartmentOptions] = useState([] as any);
  const [formData, setFormData] = useState<FormDataType>({
    first_name: singlePeople?.user.first_name,
    last_name: singlePeople?.user.last_name,
    mobile_number: singlePeople?.user.phone_number,
    email: singlePeople?.user.email,
    birthday: singlePeople?.user.dob,
    department_id: singlePeople?.department.id ?? '',
    budget: singlePeople?.budget,
    budget_duration: singlePeople?.budget_duration,
    role: singlePeople?.role,
    budget_setting: singlePeople?.budget_setting ?? ''
  });
  const handleAssignAdmin = async (value: boolean) => {
    const updatedValue = { ...formData, role: value ? 'ADMIN' : 'MEMBER' };
    setFormData(updatedValue);
    try {
      const URL = value ? 'member/send-bulk-invitation' : 'member/terminate-bulk-user-role';
      const res = await apiService.put(URL, {
        data: {
          member_ids: [singlePeople?.id]
        }
      });
      if (value && searchParams.get('from') === ROUTE_KEYS.GET_STARTED) {
        completeAssignAdminStep(companyId);
        navigate(`/${ROUTE_KEYS.GET_STARTED}`);
      }
      toast(<ToastMessage type='success' message={res.data.message} />);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleBudgetSetting = (value: string) => {
    const updateSettings = {
      ...formData,
      budget_setting: value
    };
    setFormData(updateSettings);
  };

  const getDepartment = async () => {
    if (!companyId) return;
    try {
      const res = await apiService.get(`department/company/${companyId}`);
      setDepartmentOptions(res?.data?.data);
    } catch (err) {
      handleApiError(err);
    }
  };

  useEffect(() => {
    getDepartment();
  }, [companyId]);

  const handleSelectDate = (date: Date | undefined) => {
    if (!date) return;
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate());
    setFormData({ ...formData, birthday: newDate });
  };

  const editMemberDetails = async () => {
    if (!singlePeople?.company_id) return;
    const id = singlePeople?.id;
    const data = {
      member_id: id,
      company_id: singlePeople?.company_id,
      department_id: formData.department_id,
      budget: formData.budget,
      budget_duration: formData.budget_duration,
      budget_setting: formData.budget_setting
    };
    setLoading(true);
    try {
      const res = await apiService.put(`member/update`, { data });
      if (res.status === 200) {
        toast(<ToastMessage type='success' message='Member Updated successfully' />);
        updatePerson(res.data.data);
        await refetchActiveCompany();
        onClose();
        setSelectedPeople([]);
        if (previousPage === ROUTE_KEYS.GET_STARTED) navigate(`/${ROUTE_KEYS.GET_STARTED}`);
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='w-full edit-details'>
      <div className='flex gap-3 mb-6'>
        <div
          className='w-[30px] h-[30px] rounded-full flex justify-center items-center cursor-pointer bg-[#F4F1F1]'
          onClick={() => setEditMode(false)}>
          <ArrowBackIcon />
        </div>
        <p className='text-[#2E3031] text-[20px] font-medium'>Edit details</p>
      </div>
      <div className='w-full flex gap-4'>
        <div className='w-[50%]'>
          <Input
            design='bordered'
            type='text'
            label='Name'
            disabled
            value={`${formData.first_name} ${formData.last_name}`}
          />
        </div>
        <div className='w-[50%]'>
          <Input
            design='bordered'
            type='phone'
            label='Mobile Number'
            value={formData.mobile_number}
            disabled
          />
        </div>
      </div>
      <div className='mt-5'>
        <Input design='bordered' type='text' label='Email' value={formData.email} disabled />
      </div>
      <div className='w-full'>
        <BirthdayInput disabled value={formData?.birthday} handleSelectDate={handleSelectDate} />
      </div>
      <SelectDropdown
        label='Department'
        value={selectedDepartmentName}
        setValue={(option) => {
          setFormData({ ...formData, department_id: option.id });
          setSelectedDepartmentName(option.name);
        }}
        placeholder='Select a department'
        options={departmentOptions}
      />

      <div className='flex justify-between  gap-4 mt-6'>
        <div className='w-full'>
          <CurrencyInput
            label='Set a budget'
            value={formData.budget}
            onChange={(budget) => {
              setFormData({
                ...formData,
                budget
              });
            }}
            placeholder='50,000'
          />
        </div>
        <div className='w-full mt-[-8px]'>
          <SelectDropdown
            label='Budget Duration'
            value={formData.budget_duration}
            setValue={(option) => {
              setFormData({ ...formData, budget_duration: option.name });
            }}
            placeholder='Daily'
            options={budgetDurationOptions}
          />
        </div>
      </div>
      <div className='w-full gap-4 grid grid-cols-2 ml-1 mt-6 pb-6 border-b border-b-[#EAEBEB] text-sm'>
        {/* <div className='flex items-center '>
          <Input
            type='radio'
            label='Budget as One-off'
            checked={formData.budget_setting === 'ONE OFF'}
            onChange={() => handleBudgetSetting('ONE OFF')}
          />
          <div className='inline-block pl-1'>
            <Dropdown placement='right' className='w-[200px]' tooltip content={<ToolTipIcon />}>
              <div className='w-[200px] text-gray-5 p-2 bg-white border border-gray-1 rounded'>
                Allocated amounts for each budget duration are fixed with no automatic
                replenishment.
              </div>
            </Dropdown>
          </div>
        </div> */}
        <div className='flex items-center'>
          <Input
            type='radio'
            label='Budget as roll over'
            checked={formData.budget_setting === 'ROLL OVER'}
            onChange={() => handleBudgetSetting('ROLL OVER')}
          />
          <div className='inline-block pl-1'>
            <Dropdown placement='right' className='w-[200px]' tooltip content={<ToolTipIcon />}>
              <div className='w-[200px] text-gray-5 p-2 bg-white border border-gray-1 rounded'>
                Unused budget amount automatically rolls over into the next budget duration start
                date.
              </div>
            </Dropdown>
          </div>
        </div>
        <div className='flex items-center'>
          <Input
            type='radio'
            label='Set as recurring'
            checked={formData.budget_setting === 'RECURRING'}
            onChange={() => handleBudgetSetting('RECURRING')}
          />
          <div className='inline-block pl-1'>
            <Dropdown placement='right' tooltip content={<ToolTipIcon />}>
              <div className='w-[200px] text-gray-5 p-2 bg-white border border-gray-1 rounded'>
                Automatically replenishes allocated funds at the start of each budget duration.
              </div>
            </Dropdown>
          </div>
        </div>
      </div>
      <div className='bg-[#FFF4E5] rounded-lg text-xs text-[#B26800] py-2 px-3'>
        Changes made will be updated and only take effect from the next budget start date.
      </div>
      <Switch
        active={formData.role === 'ADMIN'}
        onChange={handleAssignAdmin}
        className='mt-4'
        label={
          <div>
            <p className='text-[#2E3031] font-medium mb-1'>Assign this person as an admin</p>
            <p className='text-[#6F7376] text-sm'>
              Gravida at est volutpat suscipit. Erat massa non erat rutrum nibh mi augue.
            </p>
          </div>
        }
      />
      <div className='flex justify-end gap-4'>
        <Button
          variant='block'
          color='gray'
          onClick={() => {
            onClose();
            setSelectedPeople([]);
          }}
          className='w-[149px] h-[41px] mt-6 text-sm bg-[#F2F2F2] gray-btn'>
          Cancel
        </Button>
        <Button
          variant='block'
          color='primary'
          onClick={editMemberDetails}
          className='w-[149px] h-[41px] mt-6 text-sm'>
          {loading ? 'Loading...' : 'Confirm'}
        </Button>
      </div>
    </div>
  );
};

export default EditDetails;
